import { useEffect, useState } from 'react';
import { Collapse, Space, Typography } from 'antd';
import { CaretRightOutlined } from '@ant-design/icons';
import { PagesEnum, UserRole } from '../common/commonTypes';
import { NavLink, useLocation } from 'react-router-dom';
import VisibilityFilter from '../components/visibilityFilter';

const REPORTS_SECTION_ID = 'reports';
const REPORTS_DATA_SECTION_ID = 'reports-data';
const REFERENCES_SECTION_ID = 'references';

function Menu() {
    const location = useLocation();

    const [expanded, setExpanded] = useState<{ [key: string]: string[] }>({
        [REPORTS_SECTION_ID]: [],
        [REPORTS_DATA_SECTION_ID]: [],
        [REFERENCES_SECTION_ID]: []
    });

    const [activeGroupId, setActiveGroupId] = useState('');

    useEffect(() => {
        const path = location.pathname.toLowerCase();
        if (
            path.includes(PagesEnum.statistics) ||
            path.includes(PagesEnum.exceedanceReport) ||
            path.includes(PagesEnum.subsidyReports)
        ) {
            setExpanded((prev) => ({ ...prev, [REPORTS_SECTION_ID]: ['1'] }));
            setActiveGroupId(REPORTS_SECTION_ID);
        } else if (
            path.includes(PagesEnum.airlines) ||
            path.includes(PagesEnum.airports) ||
            path.includes(PagesEnum.cities) ||
            path.includes(PagesEnum.subsRoutes) ||
            path.includes(PagesEnum.documentTypes) ||
            path.includes(PagesEnum.passengerTypes) ||
            path.includes(PagesEnum.quotaCategories) ||
            path.includes(PagesEnum.productionCalendar) ||
            path.includes(PagesEnum.reportContracts)
        ) {
            setExpanded((prev) => ({ ...prev, [REFERENCES_SECTION_ID]: ['1'] }));
            setActiveGroupId(REFERENCES_SECTION_ID);
        } else if (
            path.includes(PagesEnum.flightRound) ||
            path.includes(PagesEnum.couponEdits) ||
            path.includes(PagesEnum.ticketEdits)
        ) {
            setExpanded((prev) => ({ ...prev, [REPORTS_DATA_SECTION_ID]: ['1'] }));
            setActiveGroupId(REPORTS_DATA_SECTION_ID);
        } else {
            setActiveGroupId('');
        }
    }, [location]);

    const handleChangeExpanded = (sectionKey: string, itemKeys: string[]) =>
        setExpanded((prev) => ({ ...prev, [sectionKey]: itemKeys }));

    const getItemGroupClassName = (groupId: string) =>
        groupId === activeGroupId ? 'navigation-item-group active' : 'navigation-item-group';

    return (
        <div className="menu">
            <Typography.Text className="menu_title">Портал субсидированных перевозок</Typography.Text>
            <Space direction="vertical" size="middle">
                <NavLink className="navigation-item navigation-item-level-0" to={PagesEnum.search}>
                    <div className="navigation-item-icon-container">
                        <div className="navigation-item-icon navigation-item-icon--data-search"></div>
                    </div>
                    <Typography.Text>Поиск данных</Typography.Text>
                </NavLink>

                <Collapse
                    className={getItemGroupClassName(REPORTS_SECTION_ID)}
                    expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} color="#303F9F" />}
                    expandIconPosition="end"
                    bordered={false}
                    ghost
                    activeKey={expanded[REPORTS_SECTION_ID]}
                    onChange={(keys) => handleChangeExpanded(REPORTS_SECTION_ID, keys as string[])}
                    items={[
                        {
                            key: '1',
                            label: (
                                <div
                                    className={
                                        activeGroupId === REPORTS_SECTION_ID
                                            ? 'navigation-item navigation-item-level-0 active'
                                            : 'navigation-item navigation-item-level-0'
                                    }
                                >
                                    <div className="navigation-item-icon-container">
                                        <div className="navigation-item-icon navigation-item-icon--stats"></div>
                                    </div>
                                    <Typography.Text>Отчеты</Typography.Text>
                                </div>
                            ),
                            children: (
                                <Space direction="vertical" size="middle">
                                    <VisibilityFilter exceptRole={UserRole.AC}>
                                        <NavLink
                                            className="navigation-item navigation-item-level-1"
                                            to={PagesEnum.statistics}
                                        >
                                            <Typography.Text>Статистика</Typography.Text>
                                        </NavLink>
                                    </VisibilityFilter>
                                    <NavLink
                                        className="navigation-item navigation-item-level-1"
                                        to={PagesEnum.exceedanceReport}
                                    >
                                        <Typography.Text>Превышение квот</Typography.Text>
                                    </NavLink>
                                    <NavLink
                                        className="navigation-item navigation-item-level-1"
                                        to={PagesEnum.subsidyReports}
                                    >
                                        <Typography.Text>Отчеты на выдачу субсидий</Typography.Text>
                                    </NavLink>
                                </Space>
                            )
                        }
                    ]}
                />

                <VisibilityFilter role={UserRole.AC}>
                    <Collapse
                        className={getItemGroupClassName(REPORTS_DATA_SECTION_ID)}
                        expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} color="#303F9F" />}
                        expandIconPosition="end"
                        bordered={false}
                        ghost
                        activeKey={expanded[REPORTS_DATA_SECTION_ID]}
                        onChange={(keys) => handleChangeExpanded(REPORTS_DATA_SECTION_ID, keys as string[])}
                        items={[
                            {
                                key: '1',
                                label: (
                                    <div
                                        className={
                                            activeGroupId === REPORTS_DATA_SECTION_ID
                                                ? 'navigation-item navigation-item-level-0 active'
                                                : 'navigation-item navigation-item-level-0'
                                        }
                                    >
                                        <div className="navigation-item-icon-container">
                                            <div className="navigation-item-icon navigation-item-icon--reports-data"></div>
                                        </div>
                                        <Typography.Text>
                                            Корректировка данных для отчетов на выдачу субсидий
                                        </Typography.Text>
                                    </div>
                                ),
                                children: (
                                    <Space direction="vertical" size="middle">
                                        <NavLink
                                            className="navigation-item navigation-item-level-1"
                                            to={PagesEnum.flightRound}
                                        >
                                            <Typography.Text>Перевозки по маршрутам</Typography.Text>
                                        </NavLink>
                                        <NavLink
                                            className="navigation-item navigation-item-level-1"
                                            to={PagesEnum.couponEdits}
                                        >
                                            <Typography.Text>Исправления в купонах</Typography.Text>
                                        </NavLink>
                                        <NavLink
                                            className="navigation-item navigation-item-level-1"
                                            to={PagesEnum.ticketEdits}
                                        >
                                            <Typography.Text>Исправления в билетах</Typography.Text>
                                        </NavLink>
                                    </Space>
                                )
                            }
                        ]}
                    />
                </VisibilityFilter>

                <NavLink className="navigation-item navigation-item-level-0" to={PagesEnum.eventLog}>
                    <div className="navigation-item-icon-container">
                        <div className="navigation-item-icon navigation-item-icon--event-log"></div>
                    </div>
                    <Typography.Text>Журнал событий ЭДО</Typography.Text>
                </NavLink>

                <VisibilityFilter role={UserRole.SIC}>
                    <NavLink className="navigation-item navigation-item-level-0" to={PagesEnum.tokens}>
                        <div className="navigation-item-icon-container">
                            <div className="navigation-item-icon navigation-item-icon--tokens"></div>
                        </div>
                        <Typography.Text>Токены API</Typography.Text>
                    </NavLink>
                </VisibilityFilter>

                <VisibilityFilter role={UserRole.SIC}>
                    <NavLink className="navigation-item navigation-item-level-0" to={PagesEnum.users}>
                        <div className="navigation-item-icon-container">
                            <div className="navigation-item-icon navigation-item-icon--users"></div>
                        </div>
                        <Typography.Text>Пользователи</Typography.Text>
                    </NavLink>
                </VisibilityFilter>

                <Collapse
                    className={getItemGroupClassName(REFERENCES_SECTION_ID)}
                    expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} color="#303F9F" />}
                    expandIconPosition="end"
                    bordered={false}
                    ghost
                    activeKey={expanded[REFERENCES_SECTION_ID]}
                    onChange={(keys) => handleChangeExpanded(REFERENCES_SECTION_ID, keys as string[])}
                    items={[
                        {
                            key: '1',
                            label: (
                                <div
                                    className={
                                        activeGroupId === REFERENCES_SECTION_ID
                                            ? 'navigation-item navigation-item-level-0 active'
                                            : 'navigation-item navigation-item-level-0'
                                    }
                                >
                                    <div className="navigation-item-icon-container">
                                        <div className="navigation-item-icon navigation-item-icon--references"></div>
                                    </div>
                                    <Typography.Text>Справочники</Typography.Text>
                                </div>
                            ),
                            children: (
                                <Space direction="vertical" size="middle">
                                    <NavLink
                                        className="navigation-item navigation-item-level-1"
                                        to={PagesEnum.reportContracts}
                                    >
                                        <Typography.Text>Договоры АК</Typography.Text>
                                    </NavLink>
                                    <NavLink
                                        className="navigation-item navigation-item-level-1"
                                        to={PagesEnum.airlines}
                                    >
                                        <Typography.Text>Авиакомпании</Typography.Text>
                                    </NavLink>
                                    <NavLink
                                        className="navigation-item navigation-item-level-1"
                                        to={PagesEnum.airports}
                                    >
                                        <Typography.Text>Аэропорты</Typography.Text>
                                    </NavLink>
                                    <NavLink className="navigation-item navigation-item-level-1" to={PagesEnum.cities}>
                                        <Typography.Text>Населенные пункты</Typography.Text>
                                    </NavLink>
                                    <NavLink
                                        className="navigation-item navigation-item-level-1"
                                        to={PagesEnum.subsRoutes}
                                    >
                                        <Typography.Text>Направления</Typography.Text>
                                    </NavLink>
                                    <NavLink
                                        className="navigation-item navigation-item-level-1"
                                        to={PagesEnum.documentTypes}
                                    >
                                        <Typography.Text>Типы документов</Typography.Text>
                                    </NavLink>
                                    <NavLink
                                        className="navigation-item navigation-item-level-1"
                                        to={PagesEnum.passengerTypes}
                                    >
                                        <Typography.Text>Типы пассажиров</Typography.Text>
                                    </NavLink>
                                    <NavLink
                                        className="navigation-item navigation-item-level-1"
                                        to={PagesEnum.quotaCategories}
                                    >
                                        <Typography.Text>Категории квотирования</Typography.Text>
                                    </NavLink>
                                    <NavLink
                                        className="navigation-item navigation-item-level-1"
                                        to={PagesEnum.productionCalendar}
                                    >
                                        <Typography.Text>Календарь</Typography.Text>
                                    </NavLink>
                                </Space>
                            )
                        }
                    ]}
                />
            </Space>
        </div>
    );
}

export default Menu;
